import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import {
  CloudDownloadOutlined, CloudUploadOutlined, WarningOutlined, CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { FONT_SIZE, FONT_WEIGHT, SPACING, RADIUS, SHADOW, ICON_SIZE, LINE_HEIGHT, isDrop, type StepStatus } from '../../constants/constants';
import { useCardPage } from '../../contexts/CardPageContext';
import { useCardEdit } from '../../contexts/CardEditContext';
import { useStockPage } from '../../contexts/StockPageContext';
import { useStorage } from '../../contexts/StorageContext';
import { READ_ONLY_REASON } from '../../constants/readOnly';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsPhone } from '../../hooks/UseIsCompact';
import {
  useWorkflow,
  type WorkflowState, type WorkflowStep, REVIEW_LIST_MISSING_REASON,
} from '../../hooks/UseWorkflow';
import { AppButton } from '../common/AppButton';

/**
 * 단계 카드가 보는 상태 — useWorkflow 의 상태에 ③ 반영 대상을 더한 것.
 * - naverCount: ③ 의 대상 건수(전체 카드 중 "참조 없음" 하락 제외, 2026-10-21). 매장 가격 요청 화면의 선택 반영(selection)은
 *   세 화면 재설계 F3(#453)로 없어졌다 — ③ 은 늘 가격 조회의 전체 반영(감사 card-controltower#490 정리).
 */
type BarState = WorkflowState & {
  naverCount: number;
  noRefCount: number;
  /**
   * ③ 반영 대상 중 공통가로 대신 받은(매장 미인식) 카드 수의 범위(감사 #181 · #314). ① 조회는 전체 건수만 주고 카드별 출처가 없어,
   * ③ 이 빼는 참조 없음 카드에 몇 건이 들었는지 모른다 — lo = 다 들었을 때, hi = 하나도 안 들었을 때(③ 대상 수를 넘지 않음).
   */
  storeUnresolved?: { lo: number; hi: number; total: number };
  /**
   * 조회 전용(VIEWER) — ① 가격 받기 · ③ 스토어 반영은 잠그고(이유 표시), ② 는 '보기' 로 연다(창 안의 적용 · 유지는 잠김).
   * 유저 플로우 제안 7(2026-11-03) — 예전엔 VIEWER 에게도 쓰기 버튼이 그대로 그려져 누르면 서버 403 만 돌아왔다.
   */
  readOnly?: boolean;
};

interface StepConfig {
  key:    WorkflowStep;
  index:  number;
  label:  string;
  desc:   (s: BarState) => string;
  status: (s: BarState) => StepStatus;
  action?: {
    label:    (s: BarState) => string;
    icon:     (s: BarState) => React.ReactNode;
    disabled: (s: BarState) => boolean;
    tooltip?: (s: BarState) => string | undefined;
  };
}

/** "N건" 또는 범위("최대 N건" · "M~N건"). */
function unresolvedRangeText(u: { lo: number; hi: number }): string {
  if (u.lo === u.hi) return `${u.hi.toLocaleString()}건`;
  if (u.lo === 0) return `최대 ${u.hi.toLocaleString()}건`;
  return `${u.lo.toLocaleString()}~${u.hi.toLocaleString()}건`;
}

/**
 * ③ 대상 기준 공통가(매장 미인식) 건수 범위(감사 card-controltower#314) — ① 조회 전체 건수(total)에서 ③ 이 빼는 참조 없음 카드
 * (noRef)를 감안한다. 카드별 출처가 없어 정확한 수 대신 범위.
 */
export function storeUnresolvedRange(total: number, naverCount: number, noRef: number): { lo: number; hi: number; total: number } {
  const hi = Math.max(0, Math.min(total, naverCount));
  const lo = Math.min(hi, Math.max(0, total - noRef));
  return { lo, hi, total };
}

const STEPS: StepConfig[] = [
  {
    // ① = 네이버 동기화 + PriceHub <매장> 가격 받기 + 시세 분류(세 화면 재설계 C10, 감사 #489). 옛 이름 '수정 가격 조회'.
    key: 'UPDATE', index: 1, label: '① 가격 받기',
    desc: (s) =>
      s.priceUpdating        ? '받는 중...'
      : s.reviewListMissing  ? '서버는 오늘 받기 완료 — 하락 검토 목록을 받지 못했습니다'
      : s.updateDone         ? '오늘 받기 완료'
      : `오늘 아직 안 받음 · 전체 ${s.totalCount.toLocaleString()}건`,
    status: (s) => s.priceUpdating ? 'active' : s.updateDone ? 'done' : 'idle',
    action: {
      label:    (s) => s.priceUpdating ? '받는 중...'
        : s.reviewListMissing ? '검토 목록 다시 받기'
        : `전체 ${s.totalCount.toLocaleString()}건 가격 받기`,
      icon:     (s) => s.priceUpdating ? <LoadingOutlined /> : <CloudDownloadOutlined />,
      // 검토 목록 다시 받기(읽기)는 조회 전용도 된다 — 체인 시작(쓰기)만 잠근다.
      disabled: (s) => s.priceUpdating || s.naverSyncing || (!!s.readOnly && !s.reviewListMissing),
      tooltip:  (s) => (s.readOnly && !s.reviewListMissing ? READ_ONLY_REASON
        : '먼저 네이버 스토어의 최신 값으로 동기화한 뒤, PriceHub 에서 이 매장 가격을 받아 타 샵 시세로 분류합니다 (Naver → 여기)'),
    },
  },
  {
    key: 'REVIEW', index: 2, label: '② 하락 검토',
    desc: (s) =>
      !s.updateDone            ? '① 가격 받기 후 활성화'
      : s.classifying          ? '타 샵 시세와 대조하는 중...'
      : s.pendingDropCount === 0? '검토·적용 완료'
      : `확인·적용 대기 ${s.pendingDropCount.toLocaleString()}건`,
    status: (s) => !s.updateDone ? 'blocked' : (s.classifying || s.pendingDropCount > 0) ? 'active' : 'done',
    action: {
      label:    (s) => s.pendingDropCount === 0 ? '검토 완료'
        : s.readOnly ? `하락 ${s.pendingDropCount.toLocaleString()}건 보기`
        : `검토·적용 (${s.pendingDropCount.toLocaleString()}건)`,
      icon:     () => <WarningOutlined />,
      disabled: (s) => !s.updateDone || s.classifying || s.pendingDropCount === 0,
      tooltip:  (s) => (s.readOnly && s.pendingDropCount > 0 ? '조회 전용 계정은 볼 수만 있습니다 — 적용 · 유지는 관리자가 합니다' : undefined),
    },
  },
  {
    key: 'NAVER', index: 3, label: '③ 스토어 반영',
    desc: (s) =>
      s.reviewListMissing     ? REVIEW_LIST_MISSING_REASON
      : !s.updateDone         ? '① 가격 받기 후 활성화'
      : s.classifying         ? '시세 대조 후 가능'
      : s.pendingDropCount > 0? `확인·적용 대기 ${s.pendingDropCount.toLocaleString()}건 처리 후 가능`
      : s.naverSyncing        ? 'Naver 반영 중...'
      : s.naverDone           ? '오늘 반영 완료'
      : s.storeUnresolved && s.storeUnresolved.hi > 0 ? `반영 준비 완료 · PriceHub 가격(매장 미인식) ${unresolvedRangeText(s.storeUnresolved)} 포함`
      : '반영 준비 완료',
    status: (s) =>
      s.reviewListMissing || !s.updateDone || s.classifying || s.pendingDropCount > 0 ? 'blocked'
      : s.naverSyncing ? 'active'
      : s.naverDone    ? 'done'
      : 'idle',
    action: {
      label:    (s) => s.naverSyncing ? '반영 중...' : `${s.naverCount.toLocaleString()}건 반영`,
      icon:     (s) => s.naverSyncing ? <LoadingOutlined /> : <CloudUploadOutlined />,
      disabled: (s) => !!s.reviewListMissing || !s.updateDone || s.classifying || s.pendingDropCount > 0 || s.naverSyncing
        || s.priceUpdating || s.naverCount === 0 || !!s.readOnly,
      tooltip:  (s) => {
        if (s.readOnly)             return READ_ONLY_REASON;
        if (s.reviewListMissing)    return REVIEW_LIST_MISSING_REASON;
        if (!s.updateDone)          return '① 가격 받기를 먼저 완료해주세요';
        if (s.classifying)          return '타 샵 시세 대조 분류가 끝나면 이어서 진행할 수 있습니다';
        if (s.pendingDropCount > 0) return `확인 필요 검토 + 시세 범위 내 일괄 적용을 마치면 반영할 수 있습니다 (대기 ${s.pendingDropCount.toLocaleString()}건)`;
        if (s.naverCount === 0)     return '반영할 카드가 없습니다';
        const u = s.storeUnresolved;
        const unresolved = u && u.hi > 0
          ? ` — 그중 ${unresolvedRangeText(u)}은 PriceHub 가 이 매장을 알아보지 못해 받은 PriceHub 가격(매장 미인식)입니다${
            u.lo !== u.hi ? `(① 가격 받기의 PriceHub 가격 ${u.total.toLocaleString()}건 중 참조 없음으로 빠진 카드는 카드별 출처가 없어 정확히 셀 수 없습니다)` : ''}`
          : '';
        return s.noRefCount > 0
          ? `이 화면에서 확인한 매장 가격을 실제 네이버 스토어에 반영합니다 (여기 → Naver) — 참조 없음 ${s.noRefCount.toLocaleString()}건은 검증할 수 없어 제외됩니다${unresolved}`
          : `이 화면에서 확인한 매장 가격을 실제 네이버 스토어에 반영합니다 (여기 → Naver)${unresolved}`;
      },
    },
  },
];

/**
 * 받는 값 없음 — 예전 "PriceHub 변경" 탭의 선택 반영(selectionTarget)은 세 화면 재설계 F3(#453)로 쓰는 곳이 없어져
 * 지웠다(감사 card-controltower#490). ③ 은 늘 전체 카드(참조 없음 제외).
 */
export type DailyWorkflowBarProps = Record<string, never>;

export const DailyWorkflowBar: React.FC<DailyWorkflowBarProps> = () => {
  const t = useThemeTokens();
  // 390px 에서는 ①~③ 단계를 한 줄에 두면 오른쪽 단계가 잘려 보이지 않았다 — 휴대폰 폭에서는
  // 단계를 위에서 아래로 쌓는다(2026-10-20 코덱 3차 지적).
  const phone = useIsPhone();
  const { setReviewOpen, cards, jobs } = useCardPage();
  const edit = useCardEdit();
  const { startSync, syncing: stockSyncing } = useStockPage();
  const { state } = useWorkflow();
  const { isAdmin } = useStorage();
  const readOnly = !isAdmin;
  const warningColor = t.warning;
  const successColor = t.success;
  const mutedColor   = t.textSecondary;

  // 오늘의 작업에서는 필터(DROP 고정)와 무관하게 전체 카드를 대상으로 작업
  const allIds = cards.cards.map((c) => c.id);
  // ③ 반영 대상 — "참조 없음"(대조할 타 샵 시세가 없어 검증 불가) 하락 카드는 뺀다(2026-10-21). 예전엔
  // 표에 "반영 제외"라고 적어 두고 실제로는 allIds 로 함께 반영돼 표기와 동작이 달랐다.
  const noRefIds = new Set(
    cards.cards.filter((c) => isDrop(c.priceChangeStatus) && c.dropBucket === 'NO_REFERENCE').map((c) => c.id),
  );
  const naverIds = allIds.filter((id) => !noRefIds.has(id));

  // 가격 조회 전에 먼저 Naver와 동기화(재고·가격·판매상태·이미지)해 최신 상태를 맞춘다 —
  // 안 그러면 오래된 로컬 값을 기준으로 가격을 조회하게 되어 위험하다(2026-07-28, 사용자 요청).
  // 동기화 "시작"과 "완료"는 별개 잡(SYNC_STOCK)이라 폴링으로 완료를 감지해야 한다 — syncing이
  // true였다가 false로 돌아오는 전환을 지켜본 뒤에만 가격 조회를 시작한다(단순히 "지금
  // syncing이 false"만 보면 동기화가 막 시작돼 아직 true로 안 바뀐 시점을 완료로 오인한다).
  const [awaitingPriceUpdate, setAwaitingPriceUpdate] = useState(false);
  const syncStartedRef = useRef(false);

  useEffect(() => {
    if (!awaitingPriceUpdate) return;
    if (stockSyncing) { syncStartedRef.current = true; return; }
    if (syncStartedRef.current) {
      syncStartedRef.current = false;
      setAwaitingPriceUpdate(false);
      jobs.startUpdatePrices(allIds);
    }
    // allIds는 렌더마다 새 배열이라 의존성에서 제외 — cards.cards 변화로 이미 갱신된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingPriceUpdate, stockSyncing, jobs, cards.cards]);

  const handlers: Record<WorkflowStep, () => void> = {
    // 전체 카드 조회 — 필터가 DROP으로 고정되어도 상승 카드까지 모두 포함
    UPDATE: async () => {
      // 서버는 오늘 받기 완료인데 하락 검토 목록을 못 받았다 — 체인을 다시 돌리지 않고 목록만 다시 받는다(감사 #656).
      if (state.reviewListMissing) { void jobs.retryReviewList?.(); return; }
      if (readOnly) return;
      // 아침 서버 체인(감사 #566, 백엔드 PR #78) — 동기화 → 가격 받기 → 시세 분류 → 상승분 반영을 서버가 이어서(창을 닫아도 계속).
      // 진행 중(409)이면 그 체인을 따라가고, 옛 서버('unsupported')일 때만 아래 예전 브라우저 사슬로.
      const chain = await jobs.startMorningChain?.();
      if (chain && chain !== 'unsupported') return;
      // ① 은 선택을 채우지 않는다(감사 card-controltower#330 — 예전 "WorkflowStatusBar 표시용" 전체 선택이 공용 선택에 남았다).
      const started = await startSync([]); // 전체 동기화
      if (started) {
        setAwaitingPriceUpdate(true); // 완료되면 위 useEffect가 이어서 가격 조회 시작
      } else {
        // 동기화 시작 자체가 실패해도 가격 조회는 막지 않는다 — 동기화는 안전을 위한
        // 사전 단계일 뿐, 필수 게이트는 아니다.
        jobs.startUpdatePrices(allIds);
      }
    },
    REVIEW: () => setReviewOpen(true),
    // 전체 카드 반영(참조 없음 제외) — 상승 카드도 포함.
    NAVER:  () => { if (!readOnly) void jobs.startSyncNaver(naverIds, edit.pendingConfirmIds); },
  };

  // 동기화(가격 조회 전 사전 단계) 중에도 STEP 1이 "받는 중"으로 보이도록 반영한다 —
  // 안 그러면 useWorkflow().state.priceUpdating은 실제 가격 조회 잡이 시작된 뒤에야 true가
  // 되므로, 동기화하는 동안 STEP 1 버튼이 다시 눌러도 되는 것처럼(idle) 보인다.
  const effectiveState: BarState = {
    ...state,
    priceUpdating: state.priceUpdating || stockSyncing || awaitingPriceUpdate,
    naverCount: naverIds.length,
    noRefCount: noRefIds.size,
    storeUnresolved: storeUnresolvedRange(jobs.storeUnresolvedCount ?? 0, naverIds.length, noRefIds.size),
    readOnly,
  };

  const allDone = state.updateDone && state.pendingDropCount === 0 && state.naverDone;
  // ③ 이 하락 확인 대기 때문에만 잠겼는가 — 그 사유 글을 ② 창 입구로(유저 플로우 제안 8). 목록 없음 · 대조 중 · ① 전은 사유가 다르다.
  const opensReview = (key: WorkflowStep) => key === 'NAVER' && !state.reviewListMissing && state.updateDone
    && !state.classifying && state.pendingDropCount > 0;

  // 템플릿 A 단계 묶음(src/layout.css .ct-steps · .ct-cell — 디자인 시스템 v2 · CT 배치 규칙 L2, 사장 결정 2026-11-04 D2): 같은 폭 세 칸 한 줄,
  // 칸마다 같은 네 줄 슬롯(머리 · 수 · 사유 · 행동)이라 옆 칸과 줄이 맞고, 행동은 칸 바닥. 본문 560 미만(휴대폰)이면 세로로(@container).
  // 예전 왼쪽 '가격 조회' 이름 칸은 페이지 제목과 겹쳐 뺐다 — 다 끝나면 위에 한 줄로 알린다.
  return (
    <div data-testid="daily-workflow-bar" className="ct-cq" data-ds="steps">
      {allDone && (
        <div data-testid="workflow-all-done" style={{
          fontSize: FONT_SIZE.sm, color: successColor, marginBottom: SPACING.xs, display: 'flex', alignItems: 'center', gap: SPACING.xxs,
        }}>
          <CheckCircleOutlined /> 오늘 가격 조회 완료
        </div>
      )}
      <ol className="ct-steps" data-n={STEPS.length} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {STEPS.map((step) => {
          const status    = step.status(effectiveState);
          const ss        = t.step(status);
          const action    = step.action;
          const isBlocked = status === 'blocked';
          const isDone    = status === 'done';
          const readOnlyNote = phone && readOnly && action?.tooltip?.(effectiveState) === READ_ONLY_REASON;

          return (
            <li
              key={step.key}
              data-testid={`workflow-step-${step.key}`}
              className="ct-cell"
              style={{
                background: ss.bg,
                border: `1px solid ${t.border}`,
                borderTop: `2px solid ${status !== 'idle' && status !== 'blocked' ? ss.border : t.border}`,
                borderRadius: RADIUS.lg,
                boxShadow: SHADOW.card,
                opacity: isBlocked ? 0.55 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              {/* ① 머리 — 번호(완료면 체크) · 단계 이름 */}
              <div className="ct-cell__head" style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
                <div style={{
                  width: ICON_SIZE.box, height: ICON_SIZE.box, borderRadius: '50%',
                  background: ss.numBg, color: ss.numColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold, flexShrink: 0,
                }}>
                  {isDone ? <CheckCircleOutlined style={{ fontSize: FONT_SIZE.sm }} /> : step.index}
                </div>
                <span style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
                  {step.label}
                </span>
              </div>
              {/* ② 수 — 지금 상태 한 줄(받기 완료 · 대기 N건 · 반영 준비 완료 …) */}
              <div className="ct-cell__count" style={{
                fontSize: FONT_SIZE.sm, lineHeight: LINE_HEIGHT.relaxed,
                color: status === 'active' ? warningColor : status === 'done' ? successColor : mutedColor,
              }}>
                {opensReview(step.key) ? (
                  // ③ 잠김 사유("확인·적용 대기 N건 처리 후 가능")를 누르면 ② 창을 연다(유저 플로우 제안 8) — 처리 입구가 ② 버튼뿐이라
                  // 사유를 읽고 다시 ② 를 찾아가야 했다. 조회 전용은 '보기' 로 열린다(창 안의 쓰기는 잠김).
                  <AppButton
                    variant="link" size="sm" data-testid="naver-blocked-open-review"
                    style={{
                      height: phone ? 44 : 'auto', padding: 0, fontSize: 'inherit', color: 'inherit',
                      textDecoration: 'underline', textAlign: 'left', whiteSpace: 'normal',
                    }}
                    onClick={handlers.REVIEW}
                  >
                    {step.desc(effectiveState)}
                  </AppButton>
                ) : step.desc(effectiveState)}
              </div>
              {/* ③ 사유 — 휴대폰은 비활성 버튼 툴팁이 잘 안 떠 조회 전용 잠금 이유를 글로(시우 검수 PR #93 낮음). 없으면 빈 자리. */}
              <div className="ct-cell__reason" style={{ color: mutedColor }}>
                {readOnlyNote && <span data-testid={`step-readonly-${step.key}`}>{READ_ONLY_REASON}</span>}
              </div>
              {/* ④ 행동 — 칸 바닥 고정 */}
              <div className="ct-cell__action">
                {action && !isBlocked && (
                  // 비활성 사유든(disabledReason) 방향 설명이든(정상 상태) 항상 hover로 보이게 Tooltip으로 감싼다 — disabledReason만 쓰면
                  // 버튼이 활성일 땐 아무 설명도 안 보여서 "동기화 후 조회"처럼 버튼 하나가 두 동작을 겸하는 걸 알 수 없었다(2026-08-07).
                  <Tooltip title={action.tooltip?.(effectiveState)}>
                    <AppButton
                      size="sm"
                      variant={status === 'active' ? 'primary' : 'default'}
                      icon={action.icon(effectiveState)}
                      disabled={action.disabled(effectiveState)}
                      onClick={handlers[step.key]}
                      style={{
                        // 휴대폰 누르는 곳 44px(감사 #518).
                        ...(phone ? { height: 44 } : {}),
                        // 활성 = 경고 채움 + 흰 글자 — warning(다크 #eab33f) 위 흰 글자는 1.91:1 이라 채움 전용 warningSolid(감사 #518).
                        ...(status === 'active' && !action.disabled(effectiveState)
                          ? { background: t.warningSolid, borderColor: t.warningSolid, color: t.white }
                          : {}),
                        ...(isDone ? { color: successColor, borderColor: successColor } : {}),
                      }}
                    >
                      {action.label(effectiveState)}
                    </AppButton>
                  </Tooltip>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};